import { createConnection, createWidget, withAmplitude } from '@livechat/widget-core-sdk';

var withCustomerProfile = function withCustomerProfile(widget) {
  var customerProfile = null;
  function onCustomerProfile(profile) {
    customerProfile = profile;
  }
  widget.on('customer_profile', onCustomerProfile);
  return Object.assign({}, widget, {
    getCustomerProfile: function getCustomerProfile() {
      return customerProfile;
    }
  });
};

// eslint-disable-next-line @typescript-eslint/ban-types
var withRichMessages = function withRichMessages(widget) {
  return Object.assign({}, widget, {
    sendQuickReplies: function sendQuickReplies(title, buttons) {
      return widget.sendMessage('send_quick_replies', {
        title: title,
        buttons: buttons
      });
    },
    sendCards: function sendCards(cards) {
      return widget.sendMessage('send_cards', Array.isArray(cards) ? cards : [cards]);
    }
  });
};

var SectionComponentType;
(function (SectionComponentType) {
  SectionComponentType["Button"] = "button";
  SectionComponentType["LabelValue"] = "label_value";
  SectionComponentType["Title"] = "title";
  SectionComponentType["Link"] = "link";
  SectionComponentType["Line"] = "line";
  SectionComponentType["Customer"] = "customer";
  SectionComponentType["Tags"] = "tags";
})(SectionComponentType || (SectionComponentType = {}));

var _componentValidators;
var AVAILABLE_COMPONENT_TYPES = Object.keys(SectionComponentType).map(function (name) {
  return SectionComponentType[name];
});
var componentValidators = (_componentValidators = {}, _componentValidators[SectionComponentType.Button] = function (_ref) {
  var data = _ref.data;
  if (!data || !data.label) {
    throw new Error("The button component must have a 'label' property");
  }
  if (!data.id) {
    throw new Error("The button component must have a 'id' property");
  }
}, _componentValidators[SectionComponentType.Link] = function (_ref2) {
  var data = _ref2.data;
  if (!data || !data.url) {
    throw new Error("The link component must have a 'url' property");
  }
}, _componentValidators[SectionComponentType.Title] = function (_ref3) {
  var data = _ref3.data;
  if (!data || !data.title) {
    throw new Error("The title component must have a 'title' property");
  }
  if (typeof data.imgSize !== 'undefined') {
    if (!['big', 'small'].includes(data.imgSize)) {
      throw new Error("The title component must have a valid 'imgSize' property, allowed values are 'small' and 'big'");
    }
  }
}, _componentValidators);
function assertComponent(component) {
  if (typeof component !== 'object') {
    throw new Error("The component definition must be an object");
  }
  var type = component.type;
  if (typeof type !== 'string' || !AVAILABLE_COMPONENT_TYPES.includes(type)) {
    throw new Error("Invalid component type given: '" + type + "'");
  }
  var validator = componentValidators[type];
  if (validator) {
    validator(component);
  }
}
function assertSection(section) {
  if (!section) {
    throw new Error('You need to provide a section defintion');
  }
  var title = section.title,
    components = section.components;
  if (typeof title !== 'string' || !title) {
    throw new Error("You need to provide a title for the new section using the 'title' property");
  }
  if (!Array.isArray(components) || !components.length) {
    throw new Error("You need to provide an array of component definitions for your section using the 'components' property");
  }
  components.forEach(assertComponent);
}

function DetailsWidget(connection) {
  var base = createWidget(connection, {
    putMessage: function putMessage(text) {
      return connection.sendMessage('put_message', text);
    },
    watchMessages: function watchMessages() {
      return connection.sendMessage('watch_messages');
    },
    refreshSession: function refreshSession() {
      return connection.sendMessage('plugin_loaded');
    },
    modifySection: function modifySection(section) {
      assertSection(section);
      return connection.sendMessage('customer_details_section', section);
    }
  });
  var widget = withAmplitude(withRichMessages(withCustomerProfile(base)));
  return widget;
}
function createDetailsWidget() {
  var widget;
  return createConnection().then(function (connection) {
    widget = DetailsWidget(connection);
    return connection.sendMessage('plugin_inited');
  }).then(function () {
    return widget;
  });
}

function MessageBoxWidget(connection) {
  var base = createWidget(connection, {
    putMessage: function putMessage(msg) {
      var data;
      if (typeof msg === 'string') {
        data = {
          type: 'message',
          value: msg
        };
      } else {
        data = {
          type: 'rich_message',
          payload: msg
        };
      }
      return connection.sendMessage('put_messagebox_message', data);
    }
  });
  var widget = withAmplitude(withRichMessages(withCustomerProfile(base)));
  return widget;
}
function createMessageBoxWidget() {
  var widget;
  return createConnection().then(function (connection) {
    widget = MessageBoxWidget(connection);
    return connection.sendMessage('plugin_inited');
  }).then(function () {
    return widget;
  });
}

function FullscreenWidget(connection) {
  var base = createWidget(connection, {
    setNotificationBadge: function setNotificationBadge(count) {
      return connection.sendMessage('set_fullscreen_widget_notification_badge', count);
    }
  });
  return withAmplitude(base);
}
function createFullscreenWidget() {
  return createConnection().then(function (connection) {
    return FullscreenWidget(connection);
  });
}

var withPageData = function withPageData(widget) {
  var pageData = null;
  function onPageData(data) {
    pageData = data;
  }
  widget.on('page_data', onPageData);
  return Object.assign({}, widget, {
    getPageData: function getPageData() {
      return pageData;
    }
  });
};

function SettingsWidget(connection) {
  var base = createWidget(connection, {
    redirect: function redirect(target) {
      return connection.sendMessage('redirect', target);
    }
  });
  return withAmplitude(withPageData(base));
}
function createSettingsWidget() {
  var widget;
  return createConnection().then(function (connection) {
    widget = SettingsWidget(connection);
    return connection.sendMessage('plugin_inited');
  }).then(function () {
    return widget;
  });
}

export { SectionComponentType, createDetailsWidget, createFullscreenWidget, createMessageBoxWidget, createSettingsWidget, withCustomerProfile };
